/**
 * Web impl of YjsStorageAdapter.
 *
 * Architecture refs:
 *   §4.5 — Web: y-indexeddb + navigator.storage.persist() + OPFS for pending
 *          media blobs. Standard Yjs persistence.
 *
 * Layout:
 *   - Yjs updates go into IndexedDB database `proof-yjs` with one object
 *     store `updates` keyed by an auto-incrementing id. Each row is
 *     `{ docId, update, seq }` and is queried by the `docId` index.
 *     Contract is append-only: saveUpdate always adds a fresh row, loadDoc
 *     returns everything for a docId in insertion (auto-id) order, deleteDoc
 *     clears every row for the docId.
 *   - `navigator.storage.persist()` is requested once per session to harden
 *     against eviction (§4.5 quirk: iOS WKWebView evicts IDB after 7 days of
 *     app-not-opened — that's why native uses SQLite, but on desktop + Android
 *     the persistence request covers most cases).
 *   - Pending media blobs live in OPFS under `proof-pending-media/<uuid>`
 *     (`putPendingMedia` / `readPendingMedia` / `deletePendingMedia`). Media
 *     upload queueing reads these back when drain()ing OfflineSyncQueue.
 *
 * The `y-indexeddb` package is retained as a peer dep for consumers who want
 * provider-style persistence attached directly to a `Y.Doc`. This adapter is
 * the *opaque key/value contract* per architecture §4.5 and is intentionally
 * independent of the provider lifecycle.
 */

import type { YjsStorageAdapter, YDocId } from "./adapter.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const DB_NAME = "proof-yjs";
const DB_VERSION = 1;
const STORE_UPDATES = "updates";
const INDEX_BY_DOC = "by-doc";

const OPFS_DIR = "proof-pending-media";

// ─── IndexedDB helpers ────────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_UPDATES)) {
        const store = db.createObjectStore(STORE_UPDATES, {
          keyPath: "seq",
          autoIncrement: true,
        });
        store.createIndex(INDEX_BY_DOC, "docId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
  });
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE_UPDATES, mode).objectStore(STORE_UPDATES);
}

function awaitReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
  });
}

// ─── Durability — navigator.storage.persist() ─────────────────────────────────

let persistRequested = false;

/**
 * Request durable storage. Called lazily on first write; idempotent across
 * the session. Resolves `true` if the browser granted persistence, `false`
 * if it refused or the API is not available. Never throws — falling back
 * to default (non-persistent) storage is acceptable.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (persistRequested) return true;
  persistRequested = true;
  try {
    if (typeof navigator !== "undefined" && navigator.storage?.persist) {
      return await navigator.storage.persist();
    }
  } catch {
    // Some browsers throw instead of returning false — swallow.
  }
  return false;
}

// ─── YjsStorageAdapter impl ───────────────────────────────────────────────────

class WebYjsStorageAdapter implements YjsStorageAdapter {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) this.dbPromise = openDb();
    return this.dbPromise;
  }

  async saveUpdate(docId: YDocId, update: Uint8Array): Promise<void> {
    // Fire-and-forget the persistence request on first write.
    void requestPersistentStorage();

    const db = await this.db();
    const store = tx(db, "readwrite");
    // Copy the update into a fresh buffer so IDB structured-clone cannot
    // share the caller's slice underlying allocation.
    const copy = new Uint8Array(update.byteLength);
    copy.set(update);
    await awaitReq(store.add({ docId, update: copy }));
  }

  async loadDoc(docId: YDocId): Promise<Uint8Array[]> {
    const db = await this.db();
    const store = tx(db, "readonly");
    const index = store.index(INDEX_BY_DOC);
    const rows = (await awaitReq(index.getAll(IDBKeyRange.only(docId)))) as Array<{
      seq: number;
      docId: string;
      update: Uint8Array;
    }>;
    // getAll returns rows in ascending key order; since `seq` is the primary
    // auto-incrementing key this is also insertion order.
    rows.sort((a, b) => a.seq - b.seq);
    return rows.map((r) => r.update);
  }

  async deleteDoc(docId: YDocId): Promise<void> {
    const db = await this.db();
    const store = tx(db, "readwrite");
    const index = store.index(INDEX_BY_DOC);
    const keys = await awaitReq(index.getAllKeys(IDBKeyRange.only(docId)));
    for (const key of keys) {
      await awaitReq(store.delete(key));
    }
  }

  async clearAll(): Promise<void> {
    // Complete wipe of every row in the `updates` store — all docs, every
    // session's worth of cached bytes. `store.clear()` is a single atomic
    // request, so this removes docs from *previous* page-loads that the
    // in-memory LRU never tracked. This is the cross-account isolation
    // guarantee on sign-out (architecture.md §4.5).
    const db = await this.db();
    const store = tx(db, "readwrite");
    await awaitReq(store.clear());
  }
}

/** Factory — the one-and-only web impl. */
export function createWebYjsStorageAdapter(): YjsStorageAdapter {
  return new WebYjsStorageAdapter();
}

// ─── OPFS — pending media blobs ───────────────────────────────────────────────
//
// Media uploads are queued through OfflineSyncQueue, but the payload (the
// actual bytes) is too large to stringify into a Request. Instead, the
// uploader writes the bytes to OPFS at `proof-pending-media/<media-uuid>`
// and enqueues only the metadata + uuid. On drain, it streams the OPFS
// file back through the API client. See Track 2.G (media uploader).

async function getOpfsDir(): Promise<FileSystemDirectoryHandle> {
  if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) {
    throw new Error("OPFS not available");
  }
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(OPFS_DIR, { create: true });
}

/** Write a pending media blob to OPFS keyed by `mediaUuid`. */
export async function putPendingMedia(mediaUuid: string, bytes: Uint8Array | Blob): Promise<void> {
  const dir = await getOpfsDir();
  const file = await dir.getFileHandle(mediaUuid, { create: true });
  const writable = await file.createWritable();
  try {
    // FileSystemWritableFileStream.write accepts Blob | BufferSource; we
    // convert Uint8Array → Blob via an intermediate ArrayBuffer copy to
    // sidestep the TS 5.7 `Uint8Array<ArrayBufferLike>` vs
    // `Uint8Array<ArrayBuffer>` variance mismatch.
    let payload: Blob;
    if (bytes instanceof Blob) {
      payload = bytes;
    } else {
      const buf = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(buf).set(bytes);
      payload = new Blob([buf]);
    }
    await writable.write(payload);
  } finally {
    await writable.close();
  }
}

/** Read a pending media blob from OPFS. Returns null if absent. */
export async function readPendingMedia(mediaUuid: string): Promise<Blob | null> {
  try {
    const dir = await getOpfsDir();
    const file = await dir.getFileHandle(mediaUuid, { create: false });
    return await file.getFile();
  } catch (err) {
    if (err instanceof DOMException && err.name === "NotFoundError") return null;
    throw err;
  }
}

/** Remove a pending media blob from OPFS. No-op if absent. */
export async function deletePendingMedia(mediaUuid: string): Promise<void> {
  try {
    const dir = await getOpfsDir();
    await dir.removeEntry(mediaUuid);
  } catch (err) {
    if (err instanceof DOMException && err.name === "NotFoundError") return;
    throw err;
  }
}
